import { compareWeights } from "./articleCode";
import { compareLocationCodes, itemWeight, labelMismatch, locationBarcode, normaliseLocationCode, normalizeProductName } from "./pickRoute";

/**
 * What goes on the label on the front of each vak (HVC-43): the vak code, large, its barcode -
 * the thing the picker scans - and the text of the bordje, "hier ligt kogelbiefstuk 250 gram".
 *
 * Pure: turns route entries into label content and sheets. The component
 * (Print/ShelfLabelSheet) only draws it.
 */

const unique = (values) => [...new Set(values)];

/** "Kogelbiefstuk 250 gram" -> "Kogelbiefstuk": the bordje text without the weight withWeightLabel put behind it. */
const withoutWeight = (text, weights) => {
  const suffix = weights.map((weight) => ` ${weight} gram`).find((ending) => text.endsWith(ending));
  return suffix ? text.slice(0, -suffix.length).trim() : text;
};

/**
 * One label per entry that has a vak code, in vak code order: 101A, 101B … 102A … 201A, the order
 * in which the cards are cut and hung, whatever the walking route does (Sam, 2026-10-23: a route
 * that goes left and right through an aisle mixed rows 1 and 2 on the sheet). Entries without a
 * code have nothing to scan yet; they are counted so the screen can say so, not silently left out.
 */
export const buildShelfLabels = (entries, { entryIds } = {}) => {
  const selected = entryIds ? new Set(entryIds) : null;
  const labels = [];
  let skipped = 0;

  (entries || [])
    .filter((entry) => !!entry && (!selected || selected.has(entry.id)))
    .forEach((entry) => {
      if (!entry.location) {
        skipped += 1;
        return;
      }
      const products = entry.products || [];
      const seenNames = new Set();
      const names = products
        .map((product) => product.name || "")
        .filter((name) => {
          const normalised = normalizeProductName(name);
          if (!normalised || seenNames.has(normalised)) return false;
          seenNames.add(normalised);
          return true;
        });
      const weights = unique(products.map(itemWeight).filter((weight) => weight !== null)).sort(compareWeights);
      // A bordje text that names something else than what lies there is not printed: the names are
      const bordje = labelMismatch(entry) ? "" : (entry.label || "").trim();
      const title = bordje || names.join(" / ") || entry.location;
      const weightText = weights.map((weight) => `${weight} gram`).join(" / ");

      labels.push({
        entryId: entry.id || null,
        sequence: entry.sequence,
        location: entry.location,
        barcodeValue: entry.barcode || locationBarcode(entry.location),
        title,
        subtitle: !!weightText && !title.includes(weightText) ? weightText : null,
        zone: entry.zone || null,
        // The product card: only for a vak something lies in, the weight on a line of its own
        productName: products.length > 0 ? withoutWeight(bordje, weights) || names.join(" / ") : null,
        productWeight: products.length > 0 && weights.length > 0 ? `${weights.join(" / ")} gram` : null,
      });
    });

  labels.sort((a, b) => compareLocationCodes(a.location, b.location));
  return { labels, skipped };
};

/**
 * The labels a sheet prints: every vak gets a vak card, but only a vak something lies in gets a
 * product card - a card on an empty basket would send the picker to look there.
 */
export const labelsForSheet = (labels, sheet) =>
  sheet && sheet.kind === "product" ? (labels || []).filter((label) => !!label.productName) : labels || [];

/**
 * How big a line of the product card can be: as big as `maxMm` allows, smaller when the text would
 * not fit on `lines` lines of `widthMm` (a bold letter is a little over half its height wide).
 */
export const fitFontSizeMm = (text, { widthMm, maxMm, minMm = 3.5, lines = 1 }) => {
  const length = Math.max(1, String(text || "").length);
  const fits = (lines * widthMm) / (0.6 * length);
  return Math.round(Math.max(minMm, Math.min(maxMm, fits)) * 10) / 10;
};

/**
 * Spread labels over sheets. `startSlot` (1-based) skips the positions of a partly used sheet,
 * so an expensive polyester sheet can be used up during data entry.
 */
export const paginateLabels = (labels, { perPage, startSlot = 1 }) => {
  const offset = Math.max(0, Math.min(perPage - 1, Math.floor(startSlot) - 1 || 0));
  const slots = [...Array(offset).fill(null), ...(labels || [])];
  const pages = [];
  for (let start = 0; start < slots.length; start += perPage) {
    const page = slots.slice(start, start + perPage);
    while (page.length < perPage) page.push(null);
    pages.push(page);
  }
  return pages;
};

/** Where a slot sits on the sheet, in millimetres from the top left. */
export const slotPosition = (slot, sheet) => {
  const column = slot % sheet.columns;
  const row = Math.floor(slot / sheet.columns);
  return {
    left: sheet.marginLeftMm + column * (sheet.labelWidthMm + sheet.gapXMm),
    top: sheet.marginTopMm + row * (sheet.labelHeightMm + sheet.gapYMm),
  };
};

// --- stellingbordjes -------------------------------------------------------------------------

const RACK_CODE = /^(\d+)([A-Z])$/;

/**
 * One sign per stelling ("Stellingbordjes"): its number, huge, for the head of the rack. A new
 * picker looks for stelling 7 from the aisle and then for the letter on the basket. A stelling is
 * the number of vak codes like 7A, 7B …; a code of another shape belongs to no stelling and is
 * counted, like a vak without a code, so the screen can say so.
 *
 * @returns {{ signs: Array<{rack, first, last, count, zones}>, skipped: number }} in stelling order
 */
export const buildRackSigns = (entries, { entryIds } = {}) => {
  const selected = entryIds ? new Set(entryIds) : null;
  const racks = new Map();
  let skipped = 0;

  (entries || [])
    .filter((entry) => !!entry && (!selected || selected.has(entry.id)))
    .forEach((entry) => {
      const match = RACK_CODE.exec(normaliseLocationCode(entry.location || ""));
      if (!match) {
        skipped += 1;
        return;
      }
      const rack = String(Number(match[1]));
      if (!racks.has(rack)) racks.set(rack, { rack, letters: new Set(), zones: new Map() });
      const sign = racks.get(rack);
      sign.letters.add(match[2]);
      if (entry.zone) sign.zones.set(entry.zone, (sign.zones.get(entry.zone) || 0) + 1);
    });

  const signs = [...racks.values()]
    .sort((a, b) => Number(a.rack) - Number(b.rack))
    .map(({ rack, letters, zones }) => {
      const sorted = [...letters].sort();
      return {
        rack,
        first: `${rack}${sorted[0]}`,
        last: `${rack}${sorted[sorted.length - 1]}`,
        count: sorted.length,
        // Most vakken first: a stelling normally lies in one zone
        zones: [...zones.entries()].sort((a, b) => b[1] - a[1]).map(([zone]) => zone),
      };
    });
  return { signs, skipped };
};

/**
 * How tall the number may be on an A4 sign: as big as the page allows, smaller for more digits
 * (a bold digit is a little over half its height wide).
 */
export const rackSignFontSizeMm = (rack) => Math.min(190, Math.floor(175 / (0.58 * Math.max(1, String(rack).length))));
